import React, { useEffect, useState } from 'react';

import '../../css/components/TruckLoanForm.css';

import { Message } from '../../utils';

const TruckReturnForm = ({
    deliverOrder,
    vehicleInfo,
    loanApplication,
    userInfo,
    onSubmit,
    onCancel
}) => {

    const statusOptions = [
        { value: 'high', label: '充足/良好', color: '#4caf50' },
        { value: 'mid', label: '中等/普通', color: '#ff9800' },
        { value: 'low', label: '低/異常', color: '#f44336' }
    ];

    const checkItems = [
        { id: 'oil', label: '油量狀態' },
        { id: 'clean', label: '清潔程度' },
        { id: 'tire', label: '胎壓' },
        { id: 'body', label: '車身外觀' }
    ];

    // 建立初始狀態物件
    const initialStatus = checkItems.reduce((acc, item) => {
        acc[item.id] = '';
        return acc;
    }, {});

    const [preStatus, setPreStatus] = useState(initialStatus);
    const [postStatus, setPostStatus] = useState(initialStatus);

    const [formData, setFormData] = useState({
        preSummary: [],
        preNote: '',
        postSummary: [],
        postNote: '',
        actualCheckoutAt: '',
        // 先上台北時間的當下，後續會改成由後端接收時自動記錄，使用dayjs轉換成台北時間的格式
        actualReturnAt: (() => {
            const now = new Date();
            const taiwanTime = new Date(now.getTime() + 8 * 60 * 60 * 1000);
            return taiwanTime.toISOString().slice(0, 16);
        })()
    });

    // 自動產生摘要
    const generateSummary = (statusObj) => {
        return checkItems
            .filter(item => statusObj[item.id])
            .map(item => {
                const option = statusOptions.find(opt => opt.value === statusObj[item.id]);
                return `${item.label}: ${option.label}`;
            });
    };

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            preSummary: generateSummary(preStatus)
        }));
    }, [preStatus]);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            postSummary: generateSummary(postStatus)
        }));
    }, [postStatus]);

    const handleSelect = (type, itemId, value) => {
        if (type === 'pre') {
            setPreStatus(prev => ({ ...prev, [itemId]: value }));
        } else {
            setPostStatus(prev => ({ ...prev, [itemId]: value }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const preAllSet = Object.values(preStatus).every(v => v !== '');
        const postAllSet = Object.values(postStatus).every(v => v !== '');

        if (!preAllSet || !postAllSet) {
            Message('請完成所有行前與行後的項目選擇', 'warning');
            return;
        }

        const finalPreCheck = [
            ...formData.preSummary,
            `補充描述: ${formData.preNote}`
        ].filter(Boolean).join('\n');

        const finalPostCheck = [
            ...formData.postSummary,
            `補充描述: ${formData.postNote}`
        ].filter(Boolean).join('\n');

        const submitData = {
            ...formData,
            preCheckStatus: finalPreCheck,
            postCheckStatus: finalPostCheck
        };

        if (onSubmit) onSubmit(submitData);
    };

    const StatusRow = ({ item, currentStatus, onSelect }) => (
        <div style={{ marginBottom: '12px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '6px', color: '#555' }}>
                {item.label}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
                {statusOptions.map(opt => {
                    const isSelected = currentStatus === opt.value;
                    return (
                        <div
                            key={opt.value}
                            onClick={() => onSelect(item.id, opt.value)}
                            style={{
                                flex: 1,
                                textAlign: 'center',
                                padding: '6px 0',
                                borderRadius: '4px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                border: `1px solid ${isSelected ? opt.color : '#ddd'}`,
                                backgroundColor: isSelected ? opt.color : '#fff',
                                color: isSelected ? '#fff' : '#666',
                                transition: 'all 0.2s'
                            }}
                        >
                            {opt.label}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="truck-loan-form-container">
            <div className="truck-loan-form-header">
                <h2>車輛借用返還紀錄表</h2>
                <span className="form-number">單號：{deliverOrder?.id || '-'}</span>
                {onCancel && (
                    <button type="button" className="btn-close-form" onClick={onCancel}>✕</button>
                )}
            </div>

            <form onSubmit={handleSubmit} className="truck-loan-form">

                <div className="form-section">
                    <h3 className="section-title">車輛資訊</h3>

                    <div className="form-row">
                        <div className="form-group">
                            <label>車牌</label>
                            <input
                                type="text"
                                value={vehicleInfo?.plate || deliverOrder?.plate || ''}
                                disabled
                                className="readonly-field"
                            />
                        </div>
                        <div className="form-group">
                            <label>車輛種類</label>
                            <input
                                type="text"
                                value={vehicleInfo?.model || ''}
                                disabled
                                className="readonly-field"
                            />
                        </div>
                        <div className="form-group">
                            <label>司機</label>
                            <input
                                type="text"
                                value={deliverOrder?.name || userInfo?.name || ''}
                                disabled
                                className="readonly-field"
                            />
                        </div>
                    </div>
                </div>

                {/* 行前 */}
                <div className="form-section">
                    {/* <h3 className="section-title">一、行車前狀況檢查 (必選)</h3> */}
                    <details open className='mb-2'>
                        <summary className="section-title clickable">一、行車前狀況選擇 (點擊收起)<span className="required">*</span></summary>
                        {checkItems.map(item => (
                            <ol><StatusRow
                                key={item.id}
                                item={item}
                                currentStatus={preStatus[item.id]}
                                onSelect={(id, val) => handleSelect('pre', id, val)}
                            /></ol>
                        ))}
                    </details>

                    <textarea
                        name="preNote"
                        value={formData.preNote}
                        onChange={handleChange}
                        placeholder="補充描述..."
                        rows={3}
                    />
                </div>

                {/* 行後 */}
                <div className="form-section">
                    {/* <h3 className="section-title">二、行車後返還檢查 (必選)</h3> */}
                    <details open className='mb-2'>
                        <summary className="section-title clickable">二、行車後返還狀況選擇 (點擊收起)<span className="required">*</span></summary>
                        {checkItems.map(item => (
                            <ol><StatusRow
                                key={item.id}
                                item={item}
                                currentStatus={postStatus[item.id]}
                                onSelect={(id, val) => handleSelect('post', id, val)}
                            /></ol>
                        ))}
                    </details>

                    <textarea
                        name="postNote"
                        value={formData.postNote}
                        onChange={handleChange}
                        placeholder="補充描述..."
                        rows={3}
                    />
                </div>

                {/* 時間記錄 */}
                <div className="form-section">
                    <div className="form-row">
                        <div className="form-group">
                            <label>出車時間 (實際出發) <span className="required">*</span></label>
                            <input
                                type="datetime-local"
                                name="actualCheckoutAt"
                                value={formData.actualCheckoutAt}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>返還時間 (系統記錄)</label>
                            <input
                                type="datetime-local"
                                name="actualReturnAt"
                                value={formData.actualReturnAt}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" className="btn-cancel" onClick={onCancel}>取消</button>
                    <button type="submit" className="btn-submit">確認歸還</button>
                </div>
            </form>
        </div>
    );
};

export { TruckReturnForm };
